import { useCallback, useMemo } from 'react';

import { useCostReviewInsuranceRecommendations } from './use-cost-review-insurance-recommendations';
import { useCostReviewRateShopperRecommendations } from './use-cost-review-rate-shopper-recommendations';
import { useCostReviewShippingServiceRecommendations } from './use-cost-review-shipping-service-recommendations';
import { useIsBulkRateShopperRecommendationsEnabled } from '../use-is-bulk-rate-shopper-recommendations-enabled';

/**
 * @typedef {import('../../prop-types/cost-review.types').CostReviewSelectedRecommendations} CostReviewSelectedRecommendations
 * @typedef {import('../../prop-types/cost-review.types').CostReviewShippingServiceRecommendationMap} CostReviewShippingServiceRecommendationMap
 * @typedef {import('../../prop-types/cost-review.types').CostReviewInsuranceRecommendationMap} CostReviewInsuranceRecommendationMap
 */

/**
 * @typedef {object} CostReviewRecommendations
 * @property {CostReviewSelectedRecommendations} selectedShippingServiceRecommendations
 * @property {CostReviewShippingServiceRecommendationMap} shippingServiceRecommendations
 * @property {(recommendationKey: string) => void} toggleShippingServiceRecommendation
 * @property {CostReviewSelectedRecommendations} selectedInsuranceRecommendations
 * @property {CostReviewInsuranceRecommendationMap} insuranceRecommendations
 * @property {(recommendationKey: string) => void} toggleInsuranceRecommendation
 */

/**
 * @param {BulkRateRecommendationSummary} recommendationSummary
 * @param {(bulkUpdateJobGuid: string, serviceRecommendationsToApply: Array<BulkApplyRateRecommendationsServiceFilter>, insuranceRecommendationsToApply: Array<BulkApplyRateRecommendationsInsuranceFilter>) => void} onApplyRecommendations
 * @returns {CostReviewRecommendations}
 */
export const useCostReviewRecommendations = (recommendationSummary, onApplyRecommendations) => {
  const isBulkRateShopperRecommendationsEnabled = useIsBulkRateShopperRecommendationsEnabled();
  const {
    selectedShippingServiceRecommendations,
    shippingServiceRecommendations,
    toggleShippingServiceRecommendation,
    selectAllShippingServiceRecommendations,
  } = useCostReviewShippingServiceRecommendations(recommendationSummary?.serviceRateRecommendations);

  const { selectedInsuranceRecommendations, insuranceRecommendations, toggleInsuranceRecommendation, selectAllInsuranceRecommendations } =
    useCostReviewInsuranceRecommendations(recommendationSummary?.insuranceRateRecommendations);

  const {
    selectedRateShopperRecommendations,
    rateShopperRecommendations,
    toggleRateShopperRecommendation,
    selectAllRateShopperRecommendations,
  } = useCostReviewRateShopperRecommendations(recommendationSummary?.rateShopperRecommendations);

  const selectAllRecommendations = useCallback(() => {
    selectAllShippingServiceRecommendations();
    selectAllInsuranceRecommendations();
    selectAllRateShopperRecommendations();
  }, [selectAllShippingServiceRecommendations, selectAllInsuranceRecommendations, selectAllRateShopperRecommendations]);

  const hasSelectedRecommendations = useMemo(() => {
    return (
      Object.values(selectedRateShopperRecommendations).some((isSelected) => isSelected) ||
      Object.values(selectedShippingServiceRecommendations).some((isSelected) => isSelected) ||
      Object.values(selectedInsuranceRecommendations).some((isSelected) => isSelected)
    );
  }, [selectedInsuranceRecommendations, selectedShippingServiceRecommendations, selectedRateShopperRecommendations]);

  const applySelectedRecommendations = useCallback(() => {
    const serviceRateRecommendations = Object.values(shippingServiceRecommendations).flatMap((services) =>
      Object.values(services).flatMap((recommendations) =>
        Object.values(recommendations).reduce((acc, recommendation) => {
          if (selectedShippingServiceRecommendations[recommendation.key]) {
            acc.push({
              fromSellerProviderId: recommendation.fromSellerProviderId,
              fromServiceId: recommendation.fromServiceId,
              toSellerProviderId: recommendation.toSellerProviderId,
              toServiceId: recommendation.toServiceId,
            });
          }
          return acc;
        }, []),
      ),
    );

    const insuranceRateRecommendations = Object.values(insuranceRecommendations).reduce((acc, recommendation) => {
      if (selectedInsuranceRecommendations[recommendation.key]) {
        acc.push({
          fromSellerProviderId: recommendation.fromSellerProviderId,
          fromInsuranceProviderId: recommendation.fromInsuranceProviderId,
          // api requires this, but does not supply it. It should be the same as the fromSellerProviderId
          toSellerProviderId: recommendation.fromSellerProviderId,
          toInsuranceProviderId: recommendation.toInsuranceProviderId,
        });
      }

      return acc;
    }, []);

    const rateShopperRecommendationsToApply = Object.values(rateShopperRecommendations).flatMap((services) =>
      Object.values(services).flatMap((recommendations) =>
        Object.values(recommendations).reduce((acc, recommendation) => {
          if (selectedRateShopperRecommendations[recommendation.key]) {
            acc.push({
              fromSellerProviderId: recommendation.fromSellerProviderId,
              fromServiceId: recommendation.fromServiceId,
              toServiceCollectionGuid: recommendation.toServiceCollectionGuid,
            });
          }
          return acc;
        }, []),
      ),
    );

    onApplyRecommendations(
      recommendationSummary?.bulkUpdateJobGuid,
      serviceRateRecommendations,
      insuranceRateRecommendations,
      rateShopperRecommendationsToApply,
      isBulkRateShopperRecommendationsEnabled,
    );
  }, [
    shippingServiceRecommendations,
    selectedShippingServiceRecommendations,
    insuranceRecommendations,
    selectedInsuranceRecommendations,
    rateShopperRecommendations,
    selectedRateShopperRecommendations,
    onApplyRecommendations,
    recommendationSummary,
    isBulkRateShopperRecommendationsEnabled,
  ]);

  return {
    selectedShippingServiceRecommendations,
    shippingServiceRecommendations,
    toggleShippingServiceRecommendation,
    selectedInsuranceRecommendations,
    insuranceRecommendations,
    toggleInsuranceRecommendation,
    selectedRateShopperRecommendations,
    rateShopperRecommendations,
    toggleRateShopperRecommendation,
    selectAllRecommendations,
    applySelectedRecommendations,
    hasSelectedRecommendations,
  };
};
